(() => {
  const leafPattern = /[\u2618\u{1F330}-\u{1F343}\u{1F96C}\u{1FAB4}]\uFE0F?/u;
  const leafPatternGlobal = /[\u2618\u{1F330}-\u{1F343}\u{1F96C}\u{1FAB4}]\uFE0F?/gu;
  const decorationHint = /(?:^|[-_\s])(falling[-_\s]*leaves?|leaves?[-_\s]*fall|autumn[-_\s]*leaves?|feuilles?|foliage|leaf[-_\s]*particles?)(?:$|[-_\s])/i;
  const candidateSelector = [
    '[class*="leaf" i]',
    '[id*="leaf" i]',
    '[class*="leaves" i]',
    '[id*="leaves" i]',
    '[class*="feuille" i]',
    '[id*="feuille" i]',
    '[class*="foliage" i]',
    '[id*="foliage" i]',
    'iframe[title*="leaf" i]',
    'iframe[src*="leaf" i]',
    'iframe[title*="leaves" i]',
    'iframe[src*="leaves" i]'
  ].join(',');

  const readHints = (element) => [
    element.id,
    element.className,
    element.getAttribute('title'),
    element.getAttribute('src'),
    element.getAttribute('data-name'),
    element.getAttribute('aria-label')
  ].filter((value) => typeof value === 'string').join(' ');

  const hideArtifact = (element) => {
    if (!(element instanceof Element)) return;
    element.classList.add('farce-leaf-artifact');
    element.setAttribute('aria-hidden', 'true');
  };

  const cleanTextNodes = (root) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const matches = [];

    while (walker.nextNode()) {
      const textNode = walker.currentNode;
      const parent = textNode.parentElement;
      if (!parent || parent.closest('script, style, noscript, textarea')) continue;
      if (leafPattern.test(textNode.nodeValue || '')) matches.push(textNode);
    }

    matches.forEach((textNode) => {
      const parent = textNode.parentElement;
      const cleaned = (textNode.nodeValue || '').replace(leafPatternGlobal, '');

      if (
        parent &&
        cleaned.replace(/[\s\u200B-\u200D\uFE0E\uFE0F]/g, '') === '' &&
        parent.textContent.replace(leafPatternGlobal, '').trim() === ''
      ) {
        hideArtifact(parent);
      } else {
        textNode.nodeValue = cleaned;
      }
    });
  };

  const cleanPseudoElements = (element) => {
    if (!(element instanceof Element)) return;

    const before = window.getComputedStyle(element, '::before').content || '';
    const after = window.getComputedStyle(element, '::after').content || '';

    if (leafPattern.test(before)) element.classList.add('farce-no-leaf-before');
    if (leafPattern.test(after)) element.classList.add('farce-no-leaf-after');
  };

  const cleanOverlayCandidate = (element) => {
    if (!(element instanceof Element)) return;

    const hints = readHints(element);
    if (decorationHint.test(hints)) {
      hideArtifact(element);
      return;
    }

    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    const touchesViewportEdge =
      rect.left <= 2 ||
      rect.right >= window.innerWidth - 2 ||
      rect.top <= 2 ||
      rect.bottom >= window.innerHeight - 2;
    const isOverlay = style.position === 'fixed' || style.position === 'absolute';
    const isNonInteractive = style.pointerEvents === 'none' || element.getAttribute('aria-hidden') === 'true';
    const leafOnly = leafPattern.test(element.textContent || '') &&
      (element.textContent || '').replace(leafPatternGlobal, '').trim() === '';
    const isTopLevelDecorationHost =
      element.parentElement === document.body &&
      isOverlay &&
      isNonInteractive &&
      touchesViewportEdge &&
      (
        element.tagName.includes('-') ||
        element.tagName === 'IFRAME' ||
        Boolean(element.shadowRoot) ||
        Boolean(element.querySelector('canvas'))
      );

    if (
      (isOverlay && touchesViewportEdge && isNonInteractive && leafOnly) ||
      isTopLevelDecorationHost
    ) {
      hideArtifact(element);
    }
  };

  const cleanRoot = (root) => {
    if (!root) return;

    if (root.nodeType === Node.TEXT_NODE) {
      const parent = root.parentElement;
      if (parent) cleanRoot(parent);
      return;
    }

    if (root instanceof Element) {
      cleanOverlayCandidate(root);
      cleanPseudoElements(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(candidateSelector).forEach(hideArtifact);
      cleanTextNodes(root);

      root.querySelectorAll('body > *, iframe, [style*="position: fixed" i], [style*="position:fixed" i]')
        .forEach((element) => {
          cleanOverlayCandidate(element);
          cleanPseudoElements(element);
        });

      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) {
          cleanRoot(element.shadowRoot);
          observeRoot(element.shadowRoot);
        }
      });
    }
  };

  const observedRoots = new WeakSet();
  const observeRoot = (root) => {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          cleanRoot(mutation.target);
          return;
        }

        if (mutation.type === 'attributes') {
          cleanRoot(mutation.target);
          return;
        }

        mutation.addedNodes.forEach(cleanRoot);
      });
    }).observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'id', 'style', 'aria-hidden']
    });
  };

  const init = () => {
    if (document.documentElement.dataset.farceNoLeavesV3 === 'true') return;
    document.documentElement.dataset.farceNoLeavesV3 = 'true';

    cleanRoot(document.body);
    observeRoot(document.documentElement);

    window.addEventListener('load', () => cleanRoot(document.body), { once: true });
    window.setTimeout(() => cleanRoot(document.body), 500);
    window.setTimeout(() => cleanRoot(document.body), 1500);
    window.setTimeout(() => cleanRoot(document.body), 4000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
